/**
 * Next / Previous over an opaque, forward-only cursor.
 *
 * The cursor arithmetic — such as it is — lives in `./cursor-stack`, which
 * explains why Previous is a stack pop and never a constructed cursor. This
 * component is the control, and it is deliberately dumb: it disables Next on
 * `next_cursor === null` and Previous at the bottom of the stack, and it reports
 * the page's own counts.
 */
export interface CursorPagerProps {
  /** `page.next_cursor` from the response. `null` means this is the last page. */
  readonly next_cursor: string | null;
  /** How many pages are behind this one. 0 disables Previous. */
  readonly historyDepth: number;
  readonly onNext: () => void;
  readonly onPrevious: () => void;
  readonly busy?: boolean;
  /** 1-based, for the "Page N" readout. */
  readonly pageNumber: number;
  readonly returnedCount: number;
  readonly totalInScope: number | null;
}

export function CursorPager({
  next_cursor,
  historyDepth,
  onNext,
  onPrevious,
  busy = false,
  pageNumber,
  returnedCount,
  totalInScope,
}: CursorPagerProps) {
  return (
    <nav className="pager" aria-label="Pagination">
      <button
        type="button"
        className="btn sm"
        onClick={onPrevious}
        disabled={busy || historyDepth === 0}
      >
        Previous
      </button>
      <p className="pager-count">
        Page {pageNumber} · {returnedCount} shown
        {totalInScope === null
          ? // Never a corpus-wide total: that would disclose the size of a set
            // the caller cannot read (PageInfoSchema).
            ' · total not counted'
          : ` of ${String(totalInScope)} in your scope`}
      </p>
      <button
        type="button"
        className="btn sm"
        onClick={onNext}
        disabled={busy || next_cursor === null}
      >
        Next
      </button>
    </nav>
  );
}
